import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';

import { LoadingPlaceholder, theme, useToggleState } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry, useTrackEffect } from '@order/Telemetry';

import { useSavedPaymentMethods } from '../../hooks';
import { AdditionalPaymentMethods } from '../AdditionalPaymentMethods';
import { PaymentMethodEditor } from '../PaymentMethodEditor';
import { SavedPaymentMethod } from '../SavedPaymentMethod';

export const SavedPaymentMethods = () => {
  const { refetchPaymentMethods, paymentMethods, isFetchingPaymentMethods } =
    useSavedPaymentMethods();

  const isFlexEnabled = useFeatureFlag('CELS-5522-flex-enabled');

  const { formatMessage } = useIntl();
  const addPaymentMethod = formatMessage(messages.addPaymentMethod);

  const {
    value: isPaymentMethodEditorVisible,
    toggleOn: showPaymentMethodEditor,
    toggleOff: hidePaymentMethodEditor,
  } = useToggleState();

  const handleClosePaymentMethodEditor = useCallback(() => {
    refetchPaymentMethods();
    hidePaymentMethodEditor();
  }, [refetchPaymentMethods, hidePaymentMethodEditor]);

  // ─── Telemetry ─────────────────────────────────────────────────────────────

  const { track } = useTelemetry();
  useTrackEffect('payments.viewed');

  const handleAddPaymentMethod = useCallback(() => {
    track('payments.new_payment.pressed');
    showPaymentMethodEditor();
  }, [track, showPaymentMethodEditor]);

  const handleEditPaymentMethod = useCallback(() => {
    track('payments.saved_payment.pressed');
    showPaymentMethodEditor();
  }, [track, showPaymentMethodEditor]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <>
      <View style={styles.container}>
        {isFetchingPaymentMethods ? (
          <LoadingPlaceholder rows={3} rowHeight={32} palette="cream" />
        ) : (
          <ScrollView contentContainerStyle={styles.scrollView}>
            <View style={styles.paymentMethods}>
              {paymentMethods.map((paymentMethod) => (
                <SavedPaymentMethod
                  key={paymentMethod.id}
                  description={paymentMethod.description}
                  icon={paymentMethod.icon}
                  style={styles.paymentMethod}
                  isDefault={paymentMethod.isDefault}
                  isExpiring={paymentMethod.isExpiring}
                  isExpired={paymentMethod.isExpired}
                  onPress={handleEditPaymentMethod}
                />
              ))}

              <SavedPaymentMethod
                icon="IconCreditCard"
                description={addPaymentMethod}
                onPress={handleAddPaymentMethod}
              />
            </View>

            {isFlexEnabled ? <AdditionalPaymentMethods /> : null}
          </ScrollView>
        )}
      </View>

      <PaymentMethodEditor
        visible={isPaymentMethodEditorVisible}
        onClose={handleClosePaymentMethodEditor}
      />
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  addPaymentMethod: {
    defaultMessage: 'Add credit or debit card',
    description: 'Saved Payment Methods | Add Payment Method',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    maxWidth: 600,
    gap: theme.spacing['4'],
  },
  scrollView: {
    flexGrow: 1,
    gap: theme.spacing['4'],
  },
  paymentMethods: {
    paddingHorizontal: theme.spacing['6'],
    backgroundColor: theme.colors.OPACITY.WHITE.DARKEST,
    borderRadius: theme.radius.large,
    borderColor: theme.colors.GRAY,
    borderWidth: 1,
  },
  paymentMethod: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.LIGHT,
  },
});
